import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  Input,
  Label,
  Modal,
  Row,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';
import InventorySelector from '../../components/Inventory/InventorySelector';
import LocationSelector from '../../components/Warehouse/locationSelector';
import UserSelector from '../../components/User/UserSelector';
import * as url from '../../helpers/url_helper';

const FILTER_TYPE = {
  USER: 'user',
  LOCATION: 'location',
  ITEM: 'item',
  ORDER: 'order',
};

function AuditTrail(props) {
  const { t } = props;

  const [selectionType, setSelectionType] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedOrderReference, setSelectedOrderReference] = useState('');
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState(null);
  const [gridSignal, setGridSignal] = useState(0);

  const resetSelections = () => {
    setSelectedUser(null);
    setSelectedLocation(null);
    setSelectedItem(null);
    setSelectedOrderReference('');
  };

  const onTypeChange = (event) => {
    const nextType = event.target.value;

    setSelectionType(nextType);
    resetSelections();
    setAppliedFilter(null);
  };

  const openSelector = () => {
    if (selectionType === FILTER_TYPE.USER) {
      setUserModalOpen(true);
    } else if (selectionType === FILTER_TYPE.LOCATION) {
      setLocationModalOpen(true);
    } else if (selectionType === FILTER_TYPE.ITEM) {
      setItemModalOpen(true);
    }
  };

  const selectedSummary = useMemo(() => {
    if (selectionType === FILTER_TYPE.USER && selectedUser !== null) {
      return selectedUser.real_name || selectedUser.display_name || selectedUser.email;
    }

    if (selectionType === FILTER_TYPE.LOCATION && selectedLocation !== null) {
      return selectedLocation.label;
    }

    if (selectionType === FILTER_TYPE.ITEM && selectedItem !== null) {
      return selectedItem.stock_id;
    }

    if (selectionType === FILTER_TYPE.ORDER) {
      return selectedOrderReference;
    }

    return '';
  }, [selectionType, selectedUser, selectedLocation, selectedItem, selectedOrderReference]);

  const buildFilter = () => {
    if (selectionType === FILTER_TYPE.USER && selectedUser !== null) {
      return `pll.user=${selectedUser.real_name || selectedUser.display_name || selectedUser.email}`;
    }

    if (selectionType === FILTER_TYPE.LOCATION && selectedLocation !== null) {
      return `pll.physical_location_label=${selectedLocation.label}`;
    }

    if (selectionType === FILTER_TYPE.ITEM && selectedItem !== null) {
      return `pll.stock_id=${selectedItem.stock_id}`;
    }

    if (selectionType === FILTER_TYPE.ORDER && selectedOrderReference.trim() !== '') {
      return `order_reference=${selectedOrderReference.trim()}`;
    }

    return null;
  };

  const applyFilter = (event) => {
    event.preventDefault();

    const nextFilter = buildFilter();
    setAppliedFilter(nextFilter);
    setGridSignal((prev) => prev + 1);
  };

  const clearFilter = () => {
    setSelectionType('');
    resetSelections();
    setAppliedFilter(null);
    setGridSignal((prev) => prev + 1);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <CardTitle className="h4">{t('Audit Trail')}</CardTitle>

                <Can i="see" this="Log">
                  <Form onSubmit={applyFilter}>
                    <Row className="align-items-end">
                      <Col md="4">
                        <div className="mb-3">
                          <Label htmlFor="auditTrailType">{t('Filter By')}</Label>
                          <Input
                            id="auditTrailType"
                            type="select"
                            value={selectionType}
                            onChange={onTypeChange}
                          >
                            <option value="">{t('Select Filter')}</option>
                            <option value={FILTER_TYPE.USER}>{t('User')}</option>
                            <option value={FILTER_TYPE.LOCATION}>{t('Physical Location')}</option>
                            <option value={FILTER_TYPE.ITEM}>{t('Item')}</option>
                            <option value={FILTER_TYPE.ORDER}>{t('Order Reference')}</option>
                          </Input>
                        </div>
                      </Col>

                      <Col md="5">
                        <div className="mb-3">
                          <Label>{t('Selection')}</Label>
                          {selectionType === FILTER_TYPE.ORDER ? (
                            <Input
                              value={selectedOrderReference}
                              placeholder={t('Enter order reference')}
                              onChange={(event) => setSelectedOrderReference(event.target.value)}
                            />
                          ) : (
                            <div className="d-flex align-items-center gap-2">
                              <Input value={selectedSummary} placeholder={t('Nothing selected')} disabled />
                              <Button
                                type="button"
                                color="light"
                                className="btn btn-primary text-nowrap"
                                disabled={selectionType === ''}
                                onClick={openSelector}
                              >
                                {t('Choose')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </Col>

                      <Col md="3">
                        <div className="mb-3 d-flex gap-2">
                          <Button color="primary" type="submit" disabled={buildFilter() === null}>
                            {t('Load Audit Trail')}
                          </Button>
                          <Button color="secondary" className="btn-soft-dark" type="button" onClick={clearFilter}>
                            {t('Clear')}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>

                  {appliedFilter === null ? (
                    <p className="text-muted mb-0">
                      {t('Choose a user, physical location, or item to load matching location log history.')}
                    </p>
                  ) : (
                    <BaseGrid
                      url={url.GET_LOCATION_LOGS}
                      filters={appliedFilter}
                      shouldUpdate={gridSignal}
                      config={{
                        columns: {
                          id: { label: 'ID', sort: true },
                          created_at: {
                            label: 'Date',
                            sort: false,
                            date: {
                              format: (date) => date.toLocaleString(),
                              timezone: process.env.REACT_APP_LOCAL_TZ,
                            },
                          },
                          physical_location_label: {
                            label: 'Location',
                            sort: false,
                          },
                          stock_id: {
                            label: 'Stock ID',
                            sort: false,
                            link: {
                              to: '/stock/%stock_id%',
                            },
                          },
                          action: {
                            label: 'Action',
                            sort: false,
                          },
                          qty: {
                            label: 'Qty',
                            sort: false,
                          },
                          user: {
                            label: 'User',
                            sort: false,
                          },
                        },
                        pagination: {
                          sort: 'id',
                          direction: 'desc',
                          perPage: 30,
                        },
                      }}
                    />
                  )}
                </Can>

                <Can not i="see" this="Log"><p>{t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={userModalOpen} toggle={() => setUserModalOpen(false)} size="lg">
          <div className="modal-header">
            <h5 className="modal-title mt-0">{t('Select User')}</h5>
            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={() => setUserModalOpen(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <UserSelector onSelect={(user) => {
              setSelectedUser(user);
              setUserModalOpen(false);
            }}
            />
          </div>
        </Modal>

        <Modal isOpen={locationModalOpen} toggle={() => setLocationModalOpen(false)} size="lg">
          <div className="modal-header">
            <h5 className="modal-title mt-0">{t('Select Physical Location')}</h5>
            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={() => setLocationModalOpen(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <LocationSelector onSelect={(location) => {
              setSelectedLocation(location);
              setLocationModalOpen(false);
            }}
            />
          </div>
        </Modal>

        <Modal isOpen={itemModalOpen} toggle={() => setItemModalOpen(false)} size="xl">
          <div className="modal-header">
            <h5 className="modal-title mt-0">{t('Select Item')}</h5>
            <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={() => setItemModalOpen(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <InventorySelector onSelect={(item) => {
              setSelectedItem(item);
              setItemModalOpen(false);
            }}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
}

AuditTrail.defaultProps = {
  t: (x) => x,
};

AuditTrail.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({}), {})(withTranslation()(AuditTrail));
